// Copyright (c) Docugami, Inc. All rights reserved.

import { AxiosInstance, AxiosRequestConfig } from "axios";
import { RequestDisabledError } from "./RequestDisabledError";
import { v4 as uuidv4 } from 'uuid';

interface TrackedRequestInfo {
    requestTimes: number[];
    disabled: boolean;
}

export interface RequestSchedulerConfig {
    disableRequestWindowMs: number;
    disableRequestThreshold: number;
}

const defaultConfig: RequestSchedulerConfig = {
    disableRequestWindowMs: 1000,
    disableRequestThreshold: 10,
}

/**
 * Implements an Axios Interceptor that performs the following behavior:
 *  1. Tracks exceptions globally for all network request errors
 *  2. Disables requests to the same endpoint that hit a configured threshold. This is always indicative of a real bug,
 *     so this buys us time to fix the underlying issue without compromising our service availability.
 *  3. Postpones requests that require authentication while there is no authenticated user
 */
export class RequestScheduler {
    private readonly _trackedRequests: Map<string, TrackedRequestInfo> = new Map();
    private _config: RequestSchedulerConfig = defaultConfig;
    private readonly _axiosInstance: AxiosInstance;

    constructor(instance: AxiosInstance, config?: Partial<RequestSchedulerConfig>) {
        this._axiosInstance = instance;
        this.schedule(config || {});
    }

    schedule(config: Partial<RequestSchedulerConfig>): () => void {
        this._config = {
            ...this._config,
            ...config
        };

        const requestToken = this._axiosInstance.interceptors.request.use(config => this.onRequest(config));
        return () => {
            this._axiosInstance.interceptors.request.eject(requestToken);
        }
    }

    private async onRequest(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
        let requestConfig = config;

        const info = this.getTrackedInfo(requestConfig);
        const requestTime = Date.now();
        const requestCount = info.requestTimes.length;

        if (info.disabled || requestCount >= this._config.disableRequestThreshold) {
            // Request has been disabled
            // Only track this error when we hit the threshold to avoid spamming our telemetry pipeline
            const trackError = !info.disabled;
            info.disabled = true;
            throw new RequestDisabledError(requestConfig.url ?? '', requestConfig.baseURL ?? '', trackError);
        }

        this.trackRequest(info, requestTime);

        return requestConfig;
    }

    private getTrackedInfo(config: AxiosRequestConfig): TrackedRequestInfo {
        let isFormData = false;
        if (!!config.headers) {
            for (const [key, value] of Object.entries(config.headers)) {
                if (key.toLowerCase() === 'content-type' && typeof (value) === 'string' && value.toLowerCase() === "multipart/form-data") {
                    isFormData = true;
                    break;
                }
            }
        }

        const key = !isFormData ? [config.method, config.url, JSON.stringify(config.params), config.data].join('&') : uuidv4();
        let info = this._trackedRequests.get(key);
        if (!info) {
            info = {
                requestTimes: [],
                disabled: false
            }

            this._trackedRequests.set(key, info);
        }

        return info;
    }

    private trackRequest(info: TrackedRequestInfo, requestTime: number) {
        for (let i = info.requestTimes.length - 1; i >= 0; --i) {
            const timeDiff = requestTime - info.requestTimes[i];
            if (timeDiff > this._config.disableRequestWindowMs) {
                // We found the first entry outside the range, so we can remove all entries
                // up until this point since we know previous entries are older
                info.requestTimes.splice(0, i + 1);
                break;
            }
        }

        info.requestTimes.push(requestTime);
    }
}
